/**
 * As preparações em curso e as falhas em aberto do processo (E200.F2, GUS-220, ADR-0143 §2).
 * Espelha `PreparacaoEmAndamento` em `apps/api/types/preparacao_do_pacote.py`.
 *
 * - `lerPreparacoes`: `GET /preparacoes`, os casos da sessão com a preparação do pacote em curso (a
 *   etapa) ou com a falha do processamento em aberto (a etapa em que parou e a causa), mais
 *   recentes primeiro. Só a equipe. É memória do processo: depois de um reinício, a lista vem
 *   vazia até a leitura de um card pedir a preparação de novo.
 *
 * A lista de casos mostra a marca na coluna Situação (`MarcaDaPreparacao`); o card do caso lê o próprio
 * pacote (`lib/preparacao_do_pacote.ts`).
 */
import type { AxiosInstance } from 'axios';

import type { EtapaDaPreparacao, FalhaDoProcessamentoData } from '@/lib/preparacao_do_pacote';

export type SituacaoDaPreparacao = 'preparando' | 'falhou';

export interface PreparacaoEmAndamentoData {
  case_id: string;
  display_code: string;
  process_number: string | null;
  pasta: string | null;
  criado_em: string;
  situacao: SituacaoDaPreparacao;
  /** Só no `preparando`: onde a preparação está agora. */
  etapa: EtapaDaPreparacao | null;
  /** Só no `falhou`: a etapa em que a rodada parou e a causa. */
  falha: FalhaDoProcessamentoData | null;
}

const ETAPAS: ReadonlySet<string> = new Set([
  'aguardando_vaga',
  'processamento',
  'saidas',
  'leituras',
]);

function ehPreparacao(data: unknown): data is PreparacaoEmAndamentoData {
  const corpo = data as Partial<PreparacaoEmAndamentoData> | null;
  if (typeof corpo !== 'object' || corpo === null) return false;
  if (typeof corpo.case_id !== 'string' || typeof corpo.display_code !== 'string') return false;
  if (corpo.situacao === 'preparando') {
    return typeof corpo.etapa === 'string' && ETAPAS.has(corpo.etapa);
  }
  return corpo.situacao === 'falhou' && typeof corpo.falha === 'object' && corpo.falha !== null;
}

export async function lerPreparacoes(client: AxiosInstance): Promise<PreparacaoEmAndamentoData[]> {
  const { data } = await client.get<unknown>('/preparacoes');
  if (!Array.isArray(data) || !data.every(ehPreparacao)) {
    throw new Error('resposta fora do contrato');
  }
  return data;
}
